import React, { useEffect, useState } from 'react';
import { Hourglass, Slash, CheckCircle2, AlertTriangle } from 'lucide-react';
import { SyncOutlined } from '@ant-design/icons';
import { message as antdMessage } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  useEnrichmentQueueStore,
  type EnrichmentQueueEntry,
} from '../../stores/useEnrichmentQueueStore';
import { cancelLinkedinSend } from '../../../modules/contacts/services/linkedinJobTracker';
import JanusEnrichingMark from '../JanusEnrichingMark';

interface Props {
  entry: EnrichmentQueueEntry;
  onAfterNavigate?: () => void;
}

const fmtElapsed = (ms: number): string => {
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s atrás`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}min atrás`;
  return `${Math.floor(min / 60)}h atrás`;
};

const isLinkedinSend = (e: EnrichmentQueueEntry): boolean => e.type === 'linkedin_send';

const labelFor = (entry: EnrichmentQueueEntry): string => {
  if (isLinkedinSend(entry)) {
    const acao = entry.sendKind === 'mensagem' ? 'Mensagem' : 'Convite';
    if (entry.status === 'processing') return `${acao} pelo LinkedIn — enviando`;
    if (entry.status === 'completed') return `${acao} pelo LinkedIn — enviado`;
    if (entry.failKind === 'not_connected') return `${acao} — aguardando aceite`;
    if (entry.failKind === 'auth_challenge') return `${acao} — reconectar LinkedIn`;
    if (entry.failKind === 'rate_limited') return `${acao} — LinkedIn pausou os envios`;
    return `${acao} pelo LinkedIn — falhou`;
  }
  if (entry.type === 'phone_search') {
    if (entry.outcome === 'phone_wait_timeout') return 'Telefone ainda não retornou';
    if (entry.status === 'processing') return 'Aguardando telefone';
    if (entry.status === 'completed') return 'Telefone disponível';
    if (entry.status === 'no_data') return 'Telefone não encontrado';
    return 'Falha na busca de telefone';
  }
  if (entry.status === 'processing') return 'Revelando contato';
  if (entry.status === 'completed') return 'Contato revelado';
  if (entry.status === 'no_data') return 'Sem dados disponíveis';
  return 'Falha no enriquecimento';
};

const iconFor = (entry: EnrichmentQueueEntry): React.ReactNode => {
  if (isLinkedinSend(entry)) {
    if (entry.status === 'processing') {
      return <SyncOutlined spin style={{ color: '#0077B5', fontSize: 16 }} />;
    }
    if (entry.status === 'completed') return <CheckCircle2 size={18} color="#16A34A" />;
    if (entry.failKind === 'not_connected') return <Hourglass size={16} color="#D97706" />;
    // Pausa da CONTA, não erro do envio — mesma cor de "aguardando", nunca a
    // vermelha de falha (que convida a reabrir/retentar).
    if (entry.failKind === 'rate_limited') return <Hourglass size={16} color="#D97706" />;
    return <AlertTriangle size={16} color="#DC2626" />;
  }
  if (entry.status === 'processing') {
    return <JanusEnrichingMark size={22} variant="light" animated state="processing" />;
  }
  if (entry.status === 'completed') {
    return <JanusEnrichingMark size={22} variant="light" animated={false} state="completed" />;
  }
  if (entry.status === 'no_data') {
    // No-data isn't an error — it's "we ran the search and the provider
    // had nothing". A neutral Slash icon reads correctly here; using
    // the failed-state Mark would imply a system error.
    return <Slash size={16} color="#94A3B8" />;
  }
  return <JanusEnrichingMark size={22} variant="light" animated={false} state="failed" />;
};

const EnrichmentQueueRow: React.FC<Props> = ({ entry, onAfterNavigate }) => {
  const navigate = useNavigate();
  const removeOnClick = useEnrichmentQueueStore((s) => s.removeOnClick);
  const [now, setNow] = useState(Date.now());
  const [blink, setBlink] = useState(false);

  // Flash newly-completed rows when the popover first opens — gives the
  // user a visual cue that this is fresh.
  useEffect(() => {
    if (entry.status !== 'processing' && !entry.viewed) {
      setBlink(true);
      const t = window.setTimeout(() => setBlink(false), 2500);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [entry.status, entry.viewed]);

  // Refresh elapsed time once a second for processing entries.
  useEffect(() => {
    if (entry.status !== 'processing') return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [entry.status]);

  const elapsedMs = (entry.completedAt ?? now) - entry.startedAt;
  // Envios LinkedIn são clicáveis mesmo enquanto processam (levam ao deal); a
  // linha só some do popover ao clicar num item JÁ resolvido (o em-voo fica).
  const linkedinSend = entry.type === 'linkedin_send';
  const clickable = linkedinSend ? Boolean(entry.dealId) : entry.status !== 'processing';

  const handleClick = () => {
    if (!clickable) return;
    onAfterNavigate?.();
    if (linkedinSend) {
      if (entry.status !== 'processing') removeOnClick(entry.messageId);
      navigate(`/pipeline/${entry.dealId}`);
      return;
    }
    removeOnClick(entry.messageId);
    navigate(`/pipeline/${entry.dealId}?openContact=${entry.contactCoreId}`);
  };

  return (
    <div
      className={blink ? 'janus-row-blink' : undefined}
      onClick={handleClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        cursor: clickable ? 'pointer' : 'default',
        borderRadius: 6,
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        if (clickable) {
          (e.currentTarget as HTMLDivElement).style.backgroundColor = '#F8FAFC';
        }
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.backgroundColor = '';
      }}
    >
      <div style={{ width: 26, display: 'flex', justifyContent: 'center' }}>
        {iconFor(entry)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {labelFor(entry)}
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--color-text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entry.label}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 2,
            fontSize: 11,
            color: 'var(--color-text-tertiary)',
          }}
        >
          <Hourglass size={10} />
          {fmtElapsed(elapsedMs)}
        </div>
      </div>
      {/* Envio LinkedIn em voo: "Cancelar" (só funciona antes de começar). */}
      {linkedinSend && entry.status === 'processing' && (
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            const r = await cancelLinkedinSend(entry.messageId);
            if (r === 'cancelled') antdMessage.success('Envio cancelado.');
            else if (r === 'requested')
              antdMessage.info('Cancelamento solicitado — se ainda der tempo, o envio é abortado.');
            else antdMessage.info('O envio já saiu e não pode mais ser cancelado.');
          }}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#DC2626',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            padding: '2px 4px',
          }}
        >
          Cancelar
        </button>
      )}
      {clickable && !(linkedinSend && entry.status === 'processing') && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#6366F1',
            whiteSpace: 'nowrap',
          }}
        >
          Abrir →
        </div>
      )}
    </div>
  );
};

export default EnrichmentQueueRow;
